import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, FileText, Trash2, MessageSquare, Brain, Shield, Loader2, ChevronRight, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = {
  mpu_gutachten: "Früheres MPU-Gutachten",
  fuehrerscheinbescheid: "Schreiben der Fahrerlaubnisbehörde",
  abstinenznachweis: "Abstinenznachweis",
  delikt_unterlagen: "Deliktbezogene Unterlagen",
  beratung_unterlagen: "Unterlagen von Beratungsstellen",
  eigene_notizen: "Eigene Notizen / vorbereitete Antworten",
  sonstige: "Sonstige Unterlagen"
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docType, setDocType] = useState("sonstige");
  const [showConsent, setShowConsent] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try { const res = await api.get("/documents"); setDocuments(res.data); } catch { toast.error("Dokumente konnten nicht geladen werden"); }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!consentGiven) { setShowConsent(true); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Datei darf maximal 10 MB groß sein"); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", docType);

    try {
      const res = await api.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.extraction_status === "no_text") {
        toast.warning(res.data.message);
      } else if (res.data.extraction_status === "error") {
        toast.error(res.data.message);
      } else {
        toast.success("Dokument hochgeladen");
      }
      fetchDocs();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const analyzeDoc = async (docId) => {
    setAnalyzing(docId);
    try {
      const res = await api.post(`/documents/${docId}/analyze`);
      toast.success("Analyse abgeschlossen");
      fetchDocs();
      setSelectedDoc(res.data);
    } catch { toast.error("Analyse fehlgeschlagen"); }
    setAnalyzing(null);
  };

  const deleteDoc = async (docId) => {
    try { await api.delete(`/documents/${docId}`); toast.success("Dokument gelöscht"); fetchDocs(); } catch { toast.error("Dokument konnte nicht gelöscht werden"); }
  };

  const startDiscussion = async (docId) => {
    try {
      const res = await api.post(`/documents/${docId}/discuss`);
      navigate(`/training/${res.data.session_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Besprechung konnte nicht gestartet werden"); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="page-transition space-y-6 max-w-4xl mx-auto" data-testid="documents-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Dokumenten-Analyse</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Laden Sie Ihre MPU-Unterlagen hoch — die KI erstellt Trainingshinweise und bespricht jeden Punkt mit Ihnen</p>
      </div>

      {/* Disclaimer */}
      <div className="bg-muted/50 rounded-xl p-4 flex items-start gap-3" data-testid="doc-disclaimer">
        <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-body text-xs text-muted-foreground leading-relaxed">
            Ihre Dokumente werden verschlüsselt verarbeitet und nach 30 Tagen automatisch gelöscht. 
            Die Analyse ist eine automatische Trainings-Hilfe und ersetzt keine professionelle Aktenprüfung, Rechtsberatung oder psychologische Beratung.
          </p>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="border-border/50 border-dashed" data-testid="upload-card">
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="sm:w-[280px] font-body" data-testid="doc-type-select">
                <SelectValue placeholder="Dokumenttyp wählen" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DOC_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k} className="font-body">{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} className="hidden" data-testid="file-input" />
              <Button
                variant="outline" className="w-full h-20 border-dashed font-body gap-2 hover:border-primary hover:bg-primary/5"
                onClick={() => { if (!consentGiven) { setShowConsent(true); } else { fileInputRef.current?.click(); } }}
                disabled={uploading} data-testid="upload-btn"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {uploading ? "Wird hochgeladen..." : "PDF, JPG oder PNG hochladen (max. 10 MB)"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      {documents.length > 0 ? (
        <div className="space-y-3">
          <h2 className="font-heading text-lg font-semibold">Ihre Dokumente</h2>
          {documents.map((doc, i) => (
            <Card key={doc.doc_id} className="border-border/50 hover-lift" data-testid={`doc-card-${i}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-body text-sm font-medium">{doc.filename}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="secondary" className="text-xs font-body">{DOC_TYPES[doc.document_type] || doc.document_type}</Badge>
                        <Badge variant={doc.status === "analyzed" ? "default" : "outline"} className="text-xs font-body">
                          {doc.status === "analyzed" ? "Analysiert" : "Hochgeladen"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.status !== "analyzed" && (
                      <Button size="sm" variant="outline" className="font-body gap-1" onClick={() => analyzeDoc(doc.doc_id)} disabled={analyzing === doc.doc_id} data-testid={`analyze-btn-${i}`}>
                        {analyzing === doc.doc_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                        Analysieren
                      </Button>
                    )}
                    {doc.status === "analyzed" && (
                      <>
                        <Button size="sm" variant="outline" className="font-body gap-1" onClick={() => setSelectedDoc(doc)} data-testid={`view-analysis-${i}`}>
                          <FileText className="w-3 h-3" /> Analyse
                        </Button>
                        <Button size="sm" className="font-body gap-1 bg-primary text-primary-foreground" onClick={() => startDiscussion(doc.doc_id)} data-testid={`discuss-btn-${i}`}>
                          <MessageSquare className="w-3 h-3" /> Besprechen
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-error" onClick={() => deleteDoc(doc.doc_id)} data-testid={`delete-doc-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-border/50"><CardContent className="py-12 text-center">
          <FileText className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="font-body text-sm text-muted-foreground">Noch keine Dokumente hochgeladen</p>
          <p className="font-body text-xs text-muted-foreground mt-1">Laden Sie Ihre MPU-Unterlagen hoch, um Trainingshinweise zu erhalten</p>
        </CardContent></Card>
      )}

      {/* Analysis Modal */}
      <Dialog open={!!selectedDoc?.analysis} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Trainingshinweise</DialogTitle>
            <DialogDescription className="font-body text-xs">Automatische Trainings-Hilfe — ersetzt keine professionelle Aktenprüfung</DialogDescription>
          </DialogHeader>
          {selectedDoc?.analysis && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-body text-sm">{selectedDoc.analysis.summary}</p>
              </div>
              {selectedDoc.analysis.key_topics?.length > 0 && (
                <div>
                  <p className="font-body text-sm font-medium mb-2">Relevante Themen:</p>
                  <div className="flex flex-wrap gap-2">{selectedDoc.analysis.key_topics.map((t, i) => <Badge key={i} variant="secondary" className="font-body text-xs">{t}</Badge>)}</div>
                </div>
              )}
              {selectedDoc.analysis.training_points?.map((tp, i) => (
                <Card key={i} className="border-border/50">
                  <CardContent className="pt-3 pb-2">
                    <p className="font-body text-sm font-medium">{tp.topic}</p>
                    <p className="font-body text-xs text-muted-foreground mt-1">{tp.description}</p>
                    {tp.possible_questions?.length > 0 && (
                      <div className="mt-2">{tp.possible_questions.map((q, j) => (
                        <div key={j} className="flex items-start gap-1.5 mt-1"><AlertTriangle className="w-3 h-3 text-warning mt-0.5 flex-shrink-0" /><p className="font-body text-xs text-muted-foreground">{q}</p></div>
                      ))}</div>
                    )}
                    {tp.training_hint && <p className="font-body text-xs text-primary mt-2"><Check className="w-3 h-3 inline mr-1" />{tp.training_hint}</p>}
                  </CardContent>
                </Card>
              ))}
              {selectedDoc.analysis.reflection_prompts?.length > 0 && (
                <div>
                  <p className="font-body text-sm font-medium mb-2">Reflexionsfragen:</p>
                  {selectedDoc.analysis.reflection_prompts.map((r, i) => (
                    <p key={i} className="font-body text-xs text-muted-foreground mb-1">- {r}</p>
                  ))}
                </div>
              )}
              <Button className="w-full font-body bg-primary text-primary-foreground gap-2" onClick={() => { setSelectedDoc(null); startDiscussion(selectedDoc.doc_id); }} data-testid="discuss-from-modal">
                <MessageSquare className="w-4 h-4" /> Jetzt gemeinsam besprechen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Consent Dialog */}
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Einwilligung zum Dokumenten-Upload</DialogTitle>
            <DialogDescription className="font-body text-sm">
              Bitte lesen Sie die folgenden Hinweise, bevor Sie Dokumente hochladen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 font-body text-sm text-muted-foreground">
            <p>Mit dem Upload Ihrer Dokumente stimmen Sie zu:</p>
            <ul className="space-y-1.5 pl-4">
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5" />Ihre Dokumente werden verschlüsselt an das KI-System übermittelt</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5" />Die Analyse dient ausschließlich als Trainings-Hilfe</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5" />Dokumente werden nach 30 Tagen automatisch gelöscht</li>
              <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-primary mt-0.5" />Sie können Dokumente jederzeit selbst löschen</li>
              <li className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5" />Die Analyse ersetzt keine professionelle Beratung</li>
            </ul>
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1 font-body" onClick={() => setShowConsent(false)}>Abbrechen</Button>
            <Button className="flex-1 font-body bg-primary text-primary-foreground" onClick={() => { setConsentGiven(true); setShowConsent(false); setTimeout(() => fileInputRef.current?.click(), 100); }} data-testid="consent-accept">
              Zustimmen & Hochladen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
